import { useEffect, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { Loader, Modal, Textarea } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DetailSection from 'src/components/DataDisplay/DetailSection/DetailSection'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  DELETE_CARRIER_CONTACTS,
  GET_ADMIN_CARRIER,
  GET_CARRIER_CONTACTS,
  UPDATE_CARRIER_CONTACT,
} from 'src/graphql/carriers.graphql'
import {
  ContactStatus,
  OUT_OF_SERVICE_CONTACT_STATUSES,
} from 'src/graphql/types/contacts'

import type { RankedCarrierContact } from '../carrier.types'
import { contactLabel } from '../carrier.utils'

import AddContactModal from './AddContactModal'
import CarrierContactsTable from './CarrierContactsTable'
import type { ContactDraft } from './ContactRowFields'

export const CONTACTS_PER_PAGE = 10

export interface CarrierContactsPanelProps {
  carrierId: string
  /** Opens the history beside this panel, filtered to one contact. */
  onViewHistory?: (contactId: string) => void
  /** Lets the history reload after a change made here. */
  onContactsChanged?: () => void
  /**
   * One of CarrierCurationStatus; decides whether adding a contact offers to
   * mark the carrier reviewed. Left out, it is read from the carrier query
   * the detail panel beside this one has already run.
   */
  curationStatus?: string | null
}

/**
 * Fetches a page of a carrier's ranked contacts and hands it to the table.
 *
 * Its own query rather than a field on the carrier: this is internal routing
 * detail, gated by @requireAuth on the query itself.
 */
const CarrierContactsPanel = ({
  carrierId,
  onViewHistory,
  onContactsChanged,
  curationStatus: curationStatusProp,
}: CarrierContactsPanelProps) => {
  // DnTable reports both the page and the rows-per-page through onLoadPage, so
  // the limit has to be held here too — pinning it would leave the control
  // visible but inert, and hide every contact past the tenth.
  const [page, setPage] = useState({ offset: 0, limit: CONTACTS_PER_PAGE })
  const [showArchived, setShowArchived] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [adding, setAdding] = useState(false)

  // The panel sits beside the carrier view rather than inside it, so nobody
  // above it holds the carrier. The detail panel's query is already in the
  // cache under these exact variables; this reads it from there and follows
  // it when a contact added here moves the carrier to REVIEWED.
  const { data: carrierData } = useQuery(GET_ADMIN_CARRIER, {
    variables: { carrierId },
    fetchPolicy: 'cache-first',
    skip: curationStatusProp !== undefined,
  })
  const curationStatus =
    curationStatusProp ?? carrierData?.carrierByCarrierId?.curationStatus

  // Named in full rather than as "everything": the query defaults to the
  // competing set, and a caller that wants the rest says which rest.
  const statuses = showArchived
    ? [ContactStatus.ACTIVE, ...OUT_OF_SERVICE_CONTACT_STATUSES]
    : [ContactStatus.ACTIVE]

  const { data, loading, error, refetch } = useQuery(GET_CARRIER_CONTACTS, {
    variables: { carrierId, filters: { statuses }, pageInfo: page },
    fetchPolicy: 'cache-and-network',
  })

  const [updateContact, { loading: savingContact }] = useMutation(
    UPDATE_CARRIER_CONTACT,
    {
      onCompleted: () => {
        toast('Contact updated', 'success')
        // An edit can merge two rows together, so the page is re-read rather
        // than patched.
        refetch()
        onContactsChanged?.()
      },
      // Handled here so the promise resolves rather than rejecting, and the
      // caller can tell a refusal from a success.
      onError: (error) => toast(error.message, 'error'),
    }
  )

  // Returns whether the save went through, so a refused edit keeps the row open
  // with the typing intact.
  const saveContact = async (contactId: string, draft: ContactDraft) => {
    const result = await updateContact({
      variables: {
        contactId,
        input: {
          contactName: draft.contactName.trim() || null,
          email: draft.email.trim() || null,
          phoneNumber: draft.phoneNumber.trim() || null,
        },
      },
    })

    return Boolean(result.data?.updateCarrierContact)
  }

  const contacts: RankedCarrierContact[] = data?.carrierContacts?.items ?? []
  const totalCount = data?.carrierContacts?.totalCount ?? 0

  // Archiving the last rows on a page leaves the reader standing past the end
  // of a list that still has contacts in it — an empty table reading "no
  // contacts on file" for a carrier that has plenty. Step back to the last
  // page that exists rather than report that.
  useEffect(() => {
    if (loading) return
    if (contacts.length > 0 || totalCount === 0 || page.offset === 0) return

    const lastPageOffset =
      Math.max(Math.ceil(totalCount / page.limit) - 1, 0) * page.limit
    setPage((current) => ({ ...current, offset: lastPageOffset }))
  }, [loading, contacts.length, totalCount, page.offset, page.limit])

  // Archiving changes where a verification goes, so it asks first. Held as ids
  // rather than as rows: the selection outlives a refetch, and the names are
  // only needed while the question is on screen.
  const [archiving, setArchiving] = useState<string[] | null>(null)
  // Cleared when the question closes, not when it opens: a reason typed for
  // one selection should not be waiting in the box for the next one.
  const [archiveNote, setArchiveNote] = useState('')
  const archivingContacts = (archiving ?? [])
    .map(
      (contactId) =>
        contacts.find((row) => row.contact.contactId === contactId)?.contact
    )
    .filter(Boolean)
  const unnamedCount = (archiving?.length ?? 0) - archivingContacts.length

  const [archiveContacts, { loading: archivingContact }] = useMutation(
    DELETE_CARRIER_CONTACTS,
    {
      onCompleted: (result) => {
        const { archived = [], skipped = [] } =
          result?.deleteCarrierContacts ?? {}
        const parts = [
          `${archived.length} contact${
            archived.length === 1 ? '' : 's'
          } archived`,
        ]
        // Named rather than counted: the rows this call left alone are the one
        // thing worth knowing about it, and "1 skipped" does not say which.
        if (skipped.length > 0) {
          parts.push(
            `${skipped
              .map((contact) => contactLabel(contact))
              .join(', ')} already archived`
          )
        }
        toast(parts.join(', '), 'success')
        setArchiving(null)
        setArchiveNote('')
        setSelectedIds(new Set())
        refetch()
        onContactsChanged?.()
      },
      onError: (error) => toast(error.message, 'error'),
    }
  )

  // The list is shorter or longer than it was, so the page it was sitting on
  // may no longer exist. Back to the first, and without the rows that are
  // about to stop being selectable.
  const changeShowArchived = (next: boolean) => {
    setShowArchived(next)
    setPage((current) => ({ ...current, offset: 0 }))
    if (!next) setSelectedIds(new Set())
  }

  if (loading && contacts.length === 0) {
    return (
      <DetailSection title="Contacts" columns={1}>
        <div className="flex items-center gap-2 py-2 text-sm text-doubleNickel-gray-500">
          <Loader size="sm" />
          Loading contacts
        </div>
      </DetailSection>
    )
  }

  // Never fall through to the table's empty state on a failure — "no contacts"
  // and "we could not load them" are different answers. Only when there is
  // nothing rendered, though: a failed background refetch should not pull a
  // good table off the screen.
  if (error && contacts.length === 0) {
    return (
      <DetailSection title="Contacts" columns={1}>
        <div className="rounded-lg border border-doubleNickel-error-200 bg-doubleNickel-error-25 p-3 text-sm text-doubleNickel-error-700">
          The contacts could not be loaded, so this is not a statement about
          what this carrier has. {error.message}
        </div>
      </DetailSection>
    )
  }

  return (
    <>
      <CarrierContactsTable
        contacts={contacts}
        totalCount={totalCount}
        pageSize={page.limit}
        // DnTable counts pages on its own, so a reset made out here has to be
        // handed to it — otherwise the rows go back to the first page while
        // the paginator still reads page three, and clicking it does nothing.
        currentPage={Math.floor(page.offset / page.limit) + 1}
        onLoadPage={setPage}
        onArchiveContacts={setArchiving}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        showArchived={showArchived}
        onShowArchivedChange={changeShowArchived}
        onSaveContact={saveContact}
        savingContact={savingContact}
        onViewHistory={onViewHistory}
        onAddContact={() => setAdding(true)}
      />
      <AddContactModal
        carrierId={carrierId}
        opened={adding}
        onClose={() => setAdding(false)}
        curationStatus={curationStatus}
        // The new row lands wherever its tier ranks it, so the page is re-read
        // rather than patched; the carrier's own count and status move too.
        onAdded={() => {
          refetch()
          onContactsChanged?.()
        }}
      />
      <Modal
        opened={Boolean(archiving?.length)}
        onClose={() => {
          setArchiving(null)
          setArchiveNote('')
        }}
        title={
          archiving?.length === 1
            ? 'Archive this contact?'
            : `Archive ${archiving?.length} contacts?`
        }
        centered
      >
        <div className="flex flex-col gap-4 text-sm">
          <div className="text-doubleNickel-gray-600">
            {archivingContacts.length === 1
              ? `${contactLabel(archivingContacts[0])} stops competing`
              : 'They stop competing'}{' '}
            to be the address a verification is sent to. Nothing is deleted:
            each contact is kept with everything it held, and the change is
            recorded in the contact history below.
          </div>
          {(archiving?.length ?? 0) > 1 && (
            <ul className="flex max-h-40 list-disc flex-col gap-1 overflow-y-auto pl-5 text-doubleNickel-gray-700">
              {archivingContacts.map((contact) => (
                <li key={contact.contactId}>{contactLabel(contact)}</li>
              ))}
              {/* A selection survives turning the page, so some of it can be
                  rows this page cannot name. Counted rather than dropped: the
                  list would otherwise be shorter than the number in the title
                  with nothing saying why. */}
              {unnamedCount > 0 && (
                <li className="text-doubleNickel-gray-500">
                  and {unnamedCount} more from another page
                </li>
              )}
            </ul>
          )}
          {/* Optional: most archivings explain themselves through the status
              change alone. The ones that do not — a dedupe, a bounced address,
              an employer asking to be taken off — are the reason this exists,
              and the history has nowhere else to carry a reason. */}
          <Textarea
            id="archive-contacts-note"
            label="Note"
            description="Optional. Why these are being archived, shown in the contact history."
            placeholder="e.g. Duplicate of the contact kept on this carrier"
            autosize
            minRows={2}
            value={archiveNote}
            onChange={(event) => setArchiveNote(event.currentTarget.value)}
          />
          <div className="flex justify-end gap-2">
            <DNButton
              variant="transparent"
              onClick={() => {
                setArchiving(null)
                setArchiveNote('')
              }}
            >
              Cancel
            </DNButton>
            <DNButton
              variant="error"
              loading={archivingContact}
              onClick={() =>
                archiveContacts({
                  variables: {
                    contactIds: archiving,
                    note: archiveNote.trim() || null,
                  },
                })
              }
            >
              Archive
            </DNButton>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default CarrierContactsPanel
